import type { UiLocale, UiStrings } from '@/constants/ui-locale'
import { fmt } from '@/constants/ui-locale'
import { formatDate, formatTime } from '@/utils/formatDateTime'
import type {
  BriefAssignedTasks,
  BriefBacklogTicket,
  BriefCommentSummary,
} from '@/services/meetingBrief'

/**
 * Presentation maths for the brief, kept out of the components.
 *
 * Everything here is pure and takes `now` as an argument: countdowns, "2 days
 * overdue" and "yesterday 17:10" are all relative to a clock the caller owns,
 * which is what keeps the screenshots and the tests from disagreeing about what
 * day it is.
 */

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function parseDateOnly(value: string): Date {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day, 12)
}

/** `20/10` for the due chip: the year is noise on a ticket due this month. */
function formatShortDate(value: Date, locale: UiLocale): string {
  return new Intl.DateTimeFormat(locale === 'ja' ? 'ja-JP' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
  }).format(value)
}

/** `dd/mm/yyyy, HH:mm – HH:mm` for the modal header. */
export function formatMeetingWindow(startsAt: string, endsAt: string, locale: UiLocale): string {
  const start = new Date(startsAt)
  const end = new Date(endsAt)
  const startLabel = `${formatDate(start, locale)}, ${formatTime(start, locale)}`
  const endLabel =
    start.toDateString() === end.toDateString()
      ? formatTime(end, locale)
      : `${formatDate(end, locale)}, ${formatTime(end, locale)}`
  return `${startLabel} – ${endLabel}`
}

/**
 * "starts in 1 hr 20 min" / "starts in 12 min" / "starting now".
 *
 * Rounds up at every boundary, because a meeting 40 seconds away is still a
 * minute away to the person reading it, and "starts in 0 min" is not a thing.
 */
export function formatCountdown(startsAt: string, now: number, t: UiStrings): string {
  const diff = new Date(startsAt).getTime() - now
  if (!Number.isFinite(diff)) return ''
  if (diff <= 0) return t.briefStartingNow

  const totalMinutes = Math.ceil(diff / 60_000)
  if (totalMinutes < 60) {
    return fmt(t.briefStartsInMinutes, { count: Math.max(1, totalMinutes) })
  }

  let hours = Math.floor(diff / 3_600_000)
  let minutes = Math.round((diff % 3_600_000) / 60_000)
  if (minutes === 60) {
    hours += 1
    minutes = 0
  }

  if (hours >= 24) {
    const days = Math.ceil(diff / 86_400_000)
    return days === 1 ? t.briefStartsInDay : fmt(t.briefStartsInDays, { count: days })
  }

  if (minutes === 0) {
    return hours === 1 ? t.briefStartsInHour : fmt(t.briefStartsInHours, { hours })
  }
  return hours === 1
    ? fmt(t.briefStartsInHourMinutes, { minutes })
    : fmt(t.briefStartsInHoursMinutes, { hours, minutes })
}

/** `Thu, 08/10/2026` / `2026/10/08(木)`, matching the reader's UI language. */
export function formatWeekdayDate(value: string | Date, locale: UiLocale): string {
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(locale === 'ja' ? 'ja-JP' : 'en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date)
}

/** "today 09:20" / "yesterday 17:10" / "3 days ago 15:40" for comment footers. */
export function formatRelativeMoment(
  value: string,
  now: number,
  locale: UiLocale,
  t: UiStrings
): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const days = Math.round(
    (startOfDay(new Date(now)).getTime() - startOfDay(date).getTime()) / 86_400_000
  )
  const time = formatTime(date, locale)
  if (days <= 0) return `${t.briefToday} ${time}`
  if (days === 1) return `${t.briefYesterday} ${time}`
  return `${fmt(t.briefDaysAgo, { count: days })} ${time}`
}

export interface DueBadge {
  label: string
  overdue: boolean
}

/** The due-date chip on a ticket row, or the completion date once it is resolved. */
export function formatDueBadge(
  ticket: BriefBacklogTicket,
  now: number,
  locale: UiLocale,
  t: UiStrings
): DueBadge | null {
  if (ticket.group === 'done' || ticket.status === 'Resolved' || ticket.status === 'Closed') {
    if (!ticket.resolvedAt) return null
    return {
      label: fmt(t.briefDoneOn, {
        date: formatShortDate(parseDateOnly(ticket.resolvedAt), locale),
      }),
      overdue: false,
    }
  }

  if (!ticket.dueDate) return null
  const due = parseDateOnly(ticket.dueDate)
  const date = formatShortDate(due, locale)
  const days = Math.round(
    (startOfDay(due).getTime() - startOfDay(new Date(now)).getTime()) / 86_400_000
  )

  if (days < 0) {
    const count = Math.abs(days)
    return {
      label:
        count === 1 ? fmt(t.briefDueOverdueOne, { date }) : fmt(t.briefDueOverdue, { date, count }),
      overdue: true,
    }
  }
  if (days === 0) return { label: t.briefDueToday, overdue: false }
  if (days === 1) return { label: fmt(t.briefDueTomorrow, { date }), overdue: false }
  return { label: fmt(t.briefDueInDays, { date, count: days }), overdue: false }
}

export function formatTicketCount(count: number, t: UiStrings): string {
  return count === 1 ? t.briefTicketCountOne : fmt(t.briefTicketCount, { count })
}

export function formatBacklogBreakdown(tickets: BriefBacklogTicket[], t: UiStrings): string {
  return fmt(t.briefBacklogBreakdown, {
    resolved: tickets.filter(
      (ticket) => ticket.group === 'done' || ticket.status === 'Resolved' || ticket.status === 'Closed'
    ).length,
    progress: tickets.filter(
      (ticket) => ticket.group !== 'done' && ticket.status === 'In Progress'
    ).length,
    open: tickets.filter(
      (ticket) =>
        ticket.group !== 'done' && ticket.status !== 'In Progress' && ticket.status !== 'Resolved'
    ).length,
  })
}

export function formatAssignedTasks(tasks: BriefAssignedTasks, t: UiStrings): string {
  if (tasks.count === 1) {
    return tasks.ticketsCreated ? t.briefAssignedCreatedOne : t.briefAssignedNotCreatedOne
  }
  return fmt(tasks.ticketsCreated ? t.briefAssignedCreated : t.briefAssignedNotCreated, {
    count: tasks.count,
  })
}

export function formatCommentMeta(
  comment: BriefCommentSummary,
  now: number,
  locale: UiLocale,
  t: UiStrings
): string {
  const count =
    comment.commentCount === 1
      ? t.briefCommentCountOne
      : fmt(t.briefCommentCount, { count: comment.commentCount })
  const people =
    comment.commenters.length > 3
      ? `${comment.commenters.slice(0, 3).join(', ')} +${comment.commenters.length - 3}`
      : comment.commenters.join(', ')
  return fmt(t.briefCommentMeta, {
    comments: count,
    people,
    when: formatRelativeMoment(comment.latestAt, now, locale, t),
  })
}

const AVATAR_TONES = [
  'border-indigo-200 bg-indigo-50 text-indigo-600 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-300',
  'border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
  'border-amber-200 bg-amber-50 text-amber-600 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300',
  'border-sky-200 bg-sky-50 text-sky-600 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300',
  'border-rose-200 bg-rose-50 text-rose-600 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300',
]

/** A stable colour per assignee, so the same person keeps the same tile. */
export function avatarTone(name: string): string {
  let total = 0
  for (let index = 0; index < name.length; index++) total += name.charCodeAt(index)
  return AVATAR_TONES[total % AVATAR_TONES.length]
}
